import '../styles/bootstrap.min.css';
import '../styles/globals.css';
import '../styles/termly-cmp.css';
import {ComponentType, ReactNode, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {PageErrorBoundary} from 'components/PageErrorBoundary';
import {CookieStorage} from 'cookie-storage';
import {theme} from 'lib/theme';
import useWindowSize from 'lib/use-window-size';
import {AuthProvider} from 'lib/useAuth';
import {useIsOverflow} from 'lib/useIsOverflow';
import {api} from 'lib/utils/api';
import {ConsentCategory, hasConsent, onConsentChange, syncHubSpotConsent} from 'utils/consent';
import {clearAnalyticsDeclined, rememberAnalyticsDeclined} from 'utils/anonymousAnalytics';
import useCookieBannerOffset from 'utils/useCookieBannerOffset';
import type {AppProps} from 'next/app';
import {useRouter} from 'next/router';
import {Provider} from 'react-redux';
import store from 'redux/store';
import {UPDATE as ABFLAGS_UPDATE} from 'redux/actions/abflagsActionTypes';
import {AnalyticsEvent} from 'redux/types/analytics';
import {debounce, Stack, ThemeProvider} from '@mui/material';
import IpProvider from '../lib/ip-context';
import chatStyles from '../styles/chat.module.scss';
import {Libraries} from '@googlemaps/js-api-loader';
import {FallbackProps} from 'react-error-boundary';
import {getAbFlags} from '@amplify/abflag';
import type {AppAbFlag} from '@amplify/athena-models';
import backupAbFlagResult from 'lib/utils/backupAbFlagResult';

declare global {
    interface Window {
        _abFlags: Record<string, boolean | string>;
    }
}

type MyAppProps = Omit<AppProps, 'Component' | 'pageProps'> & {
    pageProps: AppProps['pageProps'] & {
        abFlagScheme?: AppAbFlag[];
    };
    Component: AppProps['Component'] & {
        authenticate?: boolean;
        redirectOnAuthenticated?: boolean;
        errorEvent?: AnalyticsEvent;
        ErrorFallback?: ComponentType<FallbackProps>;
        getLayout?: (page: ReactNode) => ReactNode;
    };
};

const styles = {
    container: {
        overflow: 'auto',
        // Termly's prompt is fixed to the bottom of the viewport and covers whatever is under
        // it. useCookieBannerOffset publishes its height on <html>; the in-house banner this
        // replaced reserved a hardcoded 88px here, which was only ever right for its own
        // height. Collapses to nothing once a choice has been made.
        mb: 'var(--cookie-banner-offset, 0px)',
    },
};

export const gmapLibraries: Libraries = ['places'];

function MyApp({Component, pageProps}: MyAppProps) {
    const {width, height} = useWindowSize();
    const cookieStorage = new CookieStorage();
    const isOverflow = useIsOverflow();

    // Hydrate the abflags redux slice from the scheme fetched at build/ISR time by
    // `getStaticProps` (re-exported from `@amplify/abflag` on each page). Falls back to
    // the local backup list for pages that don't export getStaticProps or when the
    // remote fetch failed at build. Single source of truth — supersedes any in-component
    // dispatches.
    //
    // Dispatched SYNCHRONOUSLY during render (not in useEffect) so flag-gated UI like
    // verification's `useAddressV2 = !!address4620` → <AddressSelectorV2> resolves to
    // the right variant on the very first paint. A post-mount effect would let the
    // legacy variant render first and then flip — causing layout shift and ambiguous
    // A/B test attribution. The ref-guard makes the dispatch idempotent across re-renders
    // and only re-fires if pageProps.abFlagScheme changes on a page transition.
    const abFlagScheme: AppAbFlag[] = pageProps.abFlagScheme ?? backupAbFlagResult;
    const lastSeededSchemeRef = useRef<AppAbFlag[] | null>(null);
    if (lastSeededSchemeRef.current !== abFlagScheme) {
        lastSeededSchemeRef.current = abFlagScheme;
        store.dispatch({type: ABFLAGS_UPDATE, payload: getAbFlags(abFlagScheme)});
    }
    const {pathname} = useRouter();

    // Publishes --cookie-banner-offset so the layout can clear the consent prompt.
    useCookieBannerOffset();

    // Hands Termly's decision to HubSpot, which gates its own cookies from it. HubSpot is not
    // blocked here: the same loader serves the live chat this app uses.
    //
    // denyAdvertisingSignalsForGpc is deliberately not called alongside it. That helper pushes a
    // Google Consent Mode update onto window.dataLayer, and this app has no GTM, no dataLayer
    // and no advertising tags — the push would go nowhere. GPC is honoured through hasConsent,
    // which reads navigator.globalPrivacyControl directly.
    useEffect(
        () =>
            onConsentChange(({initial}) => {
                syncHubSpotConsent();

                // Only an actual decision counts. On the first run analytics is false simply
                // because nobody has chosen yet, and persisting that as a decline would silence
                // the pre-consent funnel for this visit and every visit after it.
                if (hasConsent(ConsentCategory.analytics)) clearAnalyticsDeclined();
                else if (!initial) rememberAnalyticsDeclined();
            }),
        [],
    );

    useLayoutEffect(
        debounce(() => {
            const findChatAndApplyStyles = () => {
                const chat = document.getElementById('hubspot-messages-iframe-container');

                if (chat) {
                    chat.setAttribute('class', 'widget-align-right');
                    chat.classList.add(chatStyles.chat);

                    chat.classList.remove(chatStyles.cookiesBanner);

                    if (isOverflow) {
                        chat.classList.add(chatStyles.isOverflowed);
                    } else {
                        chat.classList.remove(chatStyles.isOverflowed);
                    }

                    switch (pathname) {
                        case '/':
                            chat.classList.add(chatStyles.index);
                            break;
                        case '/verification':
                            chat.classList.add(chatStyles.verification);
                            break;
                        case '/prequalification':
                            chat.classList.add(chatStyles.prequalification);
                            break;
                        case '/additional-history':
                            chat.classList.add(chatStyles.additionalHistory);
                            break;
                        case '/employment':
                            chat.classList.add(chatStyles.employment);
                            break;
                        case '/beneficiaries':
                            chat.classList.add(chatStyles.beneficiaries);
                            break;
                        case '/submit-application':
                            chat.classList.add(chatStyles.submitApplication);
                            break;
                        case '/medical-wrap-up':
                            chat.classList.add(chatStyles.medicalWrapUp);
                            break;
                        case '/finances':
                            chat.classList.add(chatStyles.finances);
                            break;
                        case '/banking':
                            chat.classList.add(chatStyles.banking);
                            break;
                        case '/underwriting':
                            chat.classList.add(chatStyles.underwriting);
                            break;
                        case '/banking-information':
                            chat.classList.add(chatStyles.conditionalCoverage);
                            break;
                        case '/all-set':
                            chat.classList.add(chatStyles.wrapUp);
                            break;
                        default:
                            chat.classList.add(chatStyles.chat);
                            break;
                    }
                }
            };

            findChatAndApplyStyles();

            const observer = new MutationObserver(() => {
                findChatAndApplyStyles();
            });

            observer.observe(document, {
                childList: true,
                subtree: true,
            });
        }, 300),
        [width, height, isOverflow, pathname],
    );

    useEffect(() => {
        if (!Component.authenticate && !Component.redirectOnAuthenticated) {
            import(`../styles/dashboard.css`);
            return;
        }
        import('../styles/portal.css');
    }, [Component]);

    const getLayout = Component.getLayout || ((page) => page);

    return (
        <Provider store={store}>
            <ThemeProvider theme={theme}>
                <AuthProvider Component={Component} redirectUnauthenticatedTo="/">
                    <IpProvider>
                        {getLayout(
                            <PageErrorBoundary
                                errorEvent={Component.errorEvent}
                                FallbackComponent={Component.ErrorFallback}
                            >
                                <Stack sx={styles.container}>
                                    <Component {...pageProps} />
                                </Stack>
                            </PageErrorBoundary>,
                        )}
                    </IpProvider>
                </AuthProvider>
            </ThemeProvider>
        </Provider>
    );
}

export default api.withTRPC(MyApp);
